
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { jsPDF } from 'jspdf';
import ClinicLogo from '../../assets/SVG/img_logo_svg.svg';
import { useVerboseLog } from '../../utils/debug';
import { toTitleCase } from '../../utils/commonUtils';
import { useTranslation } from 'react-i18next';
interface TreatmentPlanProcedureProps {
  data: any;
  iframeRef?: React.RefObject<HTMLIFrameElement>;
}

const TreatmentPlanProcedure: React.FC<TreatmentPlanProcedureProps> = ({
  data,
  iframeRef,
}) => {
  const verboseLog = useVerboseLog();
  const { t: tReg } = useTranslation('registration_lang');
  verboseLog('data', data);
  const patient = data?.patient || {};
  const partner = patient?.partner || {};
  const stim = data?.stimulation_chart || {};
  //   const events = stim?.events || [];
  const indication = data?.procedure?.indication || '-';
  const medications = stim?.medications || [];
  const folliculars = stim?.follicular_study || [];
  const embryoImages = data?.transfer_embryo || [];
  const cryoEmbryos = data?.freezed_embryo || [];
  const outcome = data?.outcome || [];
  const summation = data?.summation || {};
  const optimum = data?.optimum_follicles || {};
  const doc = new jsPDF();
  const lineSpacing = 10;
  let y = 20;

  const section = (title: string) => {
    doc.setFontSize(14);
    doc.text(title, 14, y);
    y += lineSpacing;
    doc.setFontSize(11);
  };

  const printText = (label: string, value: string | number) => {
    doc.text(`${label}: ${value}`, 14, y);
    y += lineSpacing;
  };

  // Title
  doc.setFontSize(16);
  doc.text('IVF/ICSI Summary Report', 70, y);
  y += lineSpacing * 2;

  // 1. Patient & Partner
  section('1. Patient & Partner Details');
  printText(
    'Patient',
    `${patient.first_name} ${patient.last_name} (${patient.medical_record_no}) - ${patient.gender__name}, Age: ${patient.age}`
  );
  printText(
    'Partner',
    `${partner.first_name || '-'} ${partner.last_name || ''} (${
      partner.medical_record_no || '-'
    }) - ${partner.gender__name || '-'}, Age: ${partner.age || '-'}`
  );
  printText(
    tReg('Registration.Patient_Mobile'),
    `${patient.mobile_number_country_code ? (patient.mobile_number_country_code.startsWith('+') ? patient.mobile_number_country_code : '+' + patient.mobile_number_country_code) + ' - ' : ''}${patient.mobile_number || '-'}`
  );
  printText(
    tReg('Registration.Partner_Mobile'),
    `${partner.mobile_number_country_code ? (partner.mobile_number_country_code.startsWith('+') ? partner.mobile_number_country_code : '+' + partner.mobile_number_country_code) + ' - ' : ''}${partner.mobile_number || '-'}`
  );

  // 2. Stimulation Chart
  y += lineSpacing;
  section('2. Stimulation Chart Details');
  printText('Protocol', data?.medication_protocol || '-');
  printText('Indication', indication);
  printText('Start Date', data?.cycle_start_date || '-');

  if (medications.length > 0) {
    // doc.setFont('', 'bold');
    doc.text('Drug', 14, y);
    doc.text('Start', 60, y);
    doc.text('End', 100, y);
    doc.text('Qty', 140, y);
    y += lineSpacing;
    // doc.setFont('', 'normal');

    medications.forEach((m: any) => {
      doc.text(m.drug?.name || '-', 14, y);
      doc.text(m.start_date || '-', 60, y);
      doc.text(m.end_date || '-', 100, y);
      doc.text(String(m.quantity || '-'), 140, y);
      y += lineSpacing;
    });
  }

  // 3. Follicular Scan
  y += lineSpacing;
  section('3. Follicular Scan Details');
  folliculars.forEach((fs: any) => {
    printText('Date', fs.date);
    printText(
      'ET/RI/PSV',
      `${fs.endometrium_thickness} mm / ${fs.ri} / ${fs.psv}`
    );
    printText('Morphology', fs.endometrium_morphology);
  });

  doc.addPage('a4', 'portrait');
  // 4. Trigger Info
  y = lineSpacing;
  section('4. Trigger Information');
  medications
    .filter((m: any) => m.drug_type === 'TRIGGER')
    .forEach((m: any) => {
      printText(
        'Drug',
        `${m.drug?.name} at ${m.start_time} on ${m.start_date}`
      );
    });

  // 5. OPU
  y += lineSpacing;
  section('5. OPU Details');
  printText('Date', data?.opu_date || '-');
  printText(
    'Optimum Follicles',
    `Left: ${optimum.left_follicles || 0}, Right: ${
      optimum.right_follicles || 0
    }`
  );
  printText('Oocytes Retrieved', data?.oocytes_retrieved || '-');

  // 6. Embryology
  y += lineSpacing;
  section('6. Embryology Details');
  printText(
    'MII / MI / GV',
    `${summation.mii || 0} / ${summation.mi || 0} / ${summation.gv || 0}`
  );
  printText(
    'Fertilized / Cleaved',
    `${summation.fertilized || 0} / ${summation.cleaved || 0}`
  );
  printText(
    'Blast / Transferred / Discarded',
    `${summation.blast || 0} / ${summation.transferred || 0} / ${
      summation.discarded || 0
    }`
  );

  // 7. Embryo Images
  y += lineSpacing;
  section('7. Transferred Embryo Info');
  if (embryoImages.length === 0) {
    printText('Info', 'No embryo images available.');
  } else {
    embryoImages.forEach((e: any) => {
      printText(
        'Embryo',
        `#${e?.embryo_no} | Stage: ${e?.stage} | Grade: ${e?.grade}`
      );
    });
  }

  doc.addPage('a4', 'portrait');
  // 8. Cryopreservation
  y = lineSpacing;
  section('8. Cryopreservation Details');
  cryoEmbryos.forEach((e: any) => {
    printText(
      'Embryo',
      `#${e?.embryo_no}, Day: ${e?.day}, Grade: ${e?.grade}, Stage: ${e?.stage}`
    );
    printText(
      'Preserved',
      `Date: ${e?.cryo_preserved_date}, By: ${e?.cryo_preserved_by_name}, Location: ${e?.location_details?.hierarchy}`
    );
  });

  // 9. Outcome
  y += lineSpacing;
  section('9. Outcome Details');
  if (outcome.length === 0) {
    printText('Info', 'No outcome data available.');
  } else {
    outcome.forEach((o: any) => {
      printText(
        'Outcome',
        `Pregnancy: ${o?.pregnancy_achieved ? 'Yes' : 'No'}, Result: ${
          o?.result
        }`
      );
    });
  }

  // Save the PDF
  //  doc.save('IVF_ICSI_Summary_Report.pdf');
  verboseLog('PDF generated successfully!','success');

  const html = `<!DOCTYPE html>
        <html>
        <head>
        <meta charset="UTF-8">
        <style>
            body {
            font-family: Arial, sans-serif;
            padding: 40px;
            width: 794px;
            height: 1123px;
            }

            section {
            margin-bottom: 20px;
            }

            table {
            border-collapse: collapse;
            width: 100%;
            margin-top: 10px;
            }

            th, td {
            border: 1px solid #000;
            padding: 4px;
            text-align: left;
            }

            th {
            background-color: #f0f0f0;
            }

            .no-border th,
            .no-border td,
            .no-border thead,
            .no-border tbody {
            border: none !important;
            }

            .center {
            text-align: center !important;
            }

            @media print {
                .page-break {
                    break-before: page;
                }
            }

            .padded {
            padding: 16px 0 !important;
            }

            .top-margin {
            margin-top: 8px;
            }

            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 2px solid #ccc;
              padding-bottom: 20px;
            }

            .header .info {
              background-color: #E1E1E1;
              max-width: 60%;
              border-radius: 12px;
              padding: 16px;
            }

            .header h1 {
              margin: 0;
              font-size: 28px;
            }

            .header p {
              margin: 4px 0;
              font-size: 14px;
            }

            .header .logo {
              max-width: 288px;
            }

            .lineSpacing{
              line-height: 1.5;
              padding: 1px 0;
            }
            .patient-partner-details {
              margin-bottom: 20px;
              padding-bottom: 20px;
              border-bottom: 2px solid #ccc;
            }
            .end_report {
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              align-items: center;
            }
        </style>
        </head>

        <body>
        <div class="header">
          <div class="info">
            <h1>PureCare Clinic</h1>
            <p class="lineSpacing"></p>
              756 University Avenue, Lake Jeniferberg 35967, 46913 Farrell Burg,
              Treutelstad 64243
            </p>
            <p class="lineSpacing"><strong>Email</strong>: ramon_morissette54@gmail.com</p>
            <p class="lineSpacing"><strong>Mob</strong>: 578-778-3291 / 578-778-3291</p>
            <p class="lineSpacing"><strong>Website</strong>: purecareclinic.com</p>
          </div>
          <div class="logo">
            <img
              src=${ClinicLogo}
              alt="Logo"
              width="196px"
            />
          </div>
        </div>


        <!-- Patient & Partner Details -->
        <section class="patient-partner-details">
            <table class="no-border">
            <colgroup>
              <col style="width: 18%;">
              <col style="width: 2%;">
              <col style="width: 30%;">
              <col style="width: 18%;">
              <col style="width: 2%;">
              <col style="width: 30%;">
            </colgroup>
            <thead>
                <tr>
                <th colspan="6" class="padded center">Patient & Partner Details</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                <td style="padding-top: 12px !important;"><strong>Patient</strong></td>
                <td style="padding-top: 12px !important;"><strong>:</strong></td>
                <td style="padding-top: 12px !important;">${toTitleCase(patient.first_name ?? '')} ${toTitleCase(patient.last_name ?? '')}</td>
                <td style="padding-top: 12px !important;"><strong>Partner</strong></td>
                <td style="padding-top: 12px !important;"><strong>:</strong></td>
                <td style="padding-top: 12px !important;">${toTitleCase(partner.first_name ?? '')} ${toTitleCase(partner.last_name ?? '')}</td>
                </tr>
                <tr>
                <td><strong>Patient MR No</strong></td>
                <td><strong>:</strong></td>
                <td>${patient.medical_record_no ?? ''}</td>
                <td><strong>Partner MR No</strong></td>
                <td><strong>:</strong></td>
                <td>${partner.medical_record_no ?? ''}</td>
                </tr>
                <tr>
                <td><strong>Gender</strong></td>
                <td><strong>:</strong></td>
                <td>${patient.gender__name ?? ''}</td>
                <td><strong>Gender</strong></td>
                <td><strong>:</strong></td>
                <td>${partner.gender__name ?? ''}</td>
                </tr>
                <tr>
                <td><strong>Age</strong></td>
                <td><strong>:</strong></td>
                <td>${patient.age ?? ''}</td>
                <td><strong>Age</strong></td>
                <td><strong>:</strong></td>
                <td>${partner.age ?? ''}</td>
                </tr>
                <tr>
                <td ><strong>${tReg('Registration.Patient_Mobile')}</strong></td>
                <td><strong>:</strong></td>
                <td>${patient.mobile_number_country_code ? (patient.mobile_number_country_code.startsWith('+') ? patient.mobile_number_country_code : '+' + patient.mobile_number_country_code) +' - ' : ''}${patient.mobile_number ?? ''}</td>
                <td ><strong>${tReg('Registration.Partner_Mobile')}</strong></td>
                <td><strong>:</strong></td>
                <td>${partner.mobile_number_country_code ? (partner.mobile_number_country_code.startsWith('+') ? partner.mobile_number_country_code : '+' + partner.mobile_number_country_code) +' - ' : ''}${partner.mobile_number || ''}</td>
                </tr>
            </tbody>
            </table>
        </section>

        <!-- Stimulation Chart -->
        <section>
            <h3>1. Stimulation Chart Details</h3>
            <div style="display: flex; justify-content: flex-start; align-items: center; gap: 24px">
                <p><strong>Start Date:</strong> ${data?.cycle_start_date ?? ''}</p>
                <p><strong>Protocol:</strong> ${data?.medication_protocol ?? ''}</p>
            </div>
            <p style="margin: 0px 0px 24px"><strong>Indication:</strong> ${indication ?? ''}</p>
            <table>
            <thead>
                <tr><th>Drug</th><th>Start</th><th>End</th><th>Quantity</th></tr>
            </thead>
            <tbody>
                ${medications
    .map(
      (m: any) =>
        `<tr><td>${m.drug?.name ?? ''}</td><td>${m.start_date ?? ''}</td><td>${m.end_date ?? ''}</td><td>${m.quantity ?? ''}</td></tr>`
    )
    .join('')}
            </tbody>
            </table>
        </section>

        <!-- Follicular Scan -->
        <section>
            <h3>2. Follicular Scan Details</h3>
            <table>
            <thead>
                <tr><th>Date</th><th>ET</th><th>RI</th><th>PSV</th><th>Morphology</th></tr>
            </thead>
            <tbody>
                ${folliculars
    .map(
      (fs: any) =>
        `<tr><td>${fs.date ?? ''}</td><td>${fs.endometrium_thickness ?? ''} mm</td><td>${fs.ri ?? ''}</td><td>${fs.psv ?? ''}</td><td>${fs.endometrium_morphology ?? ''}</td></tr>`
    )
    .join('')}
            </tbody>
            </table>
        </section>

        <!-- Trigger Info -->
        <section>
            <h3>3. Trigger Information</h3>
            <table>
            <thead>
                <tr><th>Drug Name</th><th>Date </th><th>Time</th></tr>
            </thead>
            <tbody>
                ${medications
    .filter((m: any) => m.drug_type === 'TRIGGER')
    .map(
      (m: any) =>
        `<tr><td>${m.drug?.name ?? ''}</td><td>${m.start_date ?? ''}</td><td>${m.start_time ?? ''}</td></tr>`
    )
    .join('')}
            </tbody>
            </table>
        </section>

        <!-- OPU Details -->
        <section>
            <h3>4. OPU Details</h3>
            <table>
            <thead>
                <tr>
                <th rowspan="2">OPU Date</th>
                <th colspan="2" class="center">Optimum Follicles</th>
                <th rowspan="2">Oocytes Retrieved</th>
                </tr>
                <tr>
                <th>Left</th>
                <th>Right</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                <td>${data?.opu_date || ''}</td>
                <td>${optimum?.left_follicles || 0}</td>
                <td>${optimum?.right_follicles || 0}</td>
                <td>${data?.oocytes_retrieved || 0}</td>
                </tr>
            </tbody>
            </table>
        </section>

        <!-- Embryology -->
        <section class="page-break">
            <h3>5. Embryology Details</h3>
            <table>
            <thead>
                <tr><th>MII</th><th>MI</th><th>GV</th><th>Fertilized</th><th>Cleaved</th><th>Blast</th><th>Transferred</th><th>Discarded</th></tr>
            </thead>
            <tbody>
                <tr>
                <td>${summation.mii ?? ''}</td>
                <td>${summation.mi ?? ''}</td>
                <td>${summation.gv  ?? ''}</td>
                <td>${summation.fertilized  ?? ''}</td>
                <td>${summation.cleaved  ?? ''}</td>
                <td>${summation.blast  ?? ''}</td>
                <td>${summation.transferred  ?? ''}</td>
                <td>${summation.discarded  ?? ''}</td>
                </tr>
            </tbody>
            </table>
        </section>

        <!-- Embryo Images -->
        <section>
            <h3>6. Transferred Embryo Images</h3>
            <table>
            <thead>
                <tr><th>Embryo No</th><th>Stage</th><th>Grade</th></tr>
            </thead>
            <tbody>
                ${embryoImages
    .map(
      (e: any) =>
        `<tr><td>${e.embryo_no ?? ''}</td><td>${e.stage ?? ''}</td><td>${e.grade ?? ''}</td></tr>`
    )
    .join('')}
            </tbody>
            </table>
        </section>

        <!-- Cryopreservation -->
        <section>
            <h3>7. Cryopreservation Details</h3>
            <table>
            <thead>
                <tr><th>Embryo No</th><th>Day</th><th>Grade</th><th>Stage</th><th>Preserved Date</th><th>By</th><th>Location</th></tr>
            </thead>
            <tbody>
                ${cryoEmbryos
    .map(
      (e: any) =>
        `<tr><td>${e.embryo_no ?? ''}</td><td>${e.day ?? ''}</td><td>${e.grade ?? ''}</td><td>${e.stage ?? ''}</td><td>${e.cryo_preserved_date ?? ''}</td><td>${e.cryo_preserved_by_name ?? ''}</td><td>${e.location_details?.hierarchy ?? ''}</td></tr>`
    )
    .join('')}
            </tbody>
            </table>
        </section>

        <!-- Outcome -->
        <section style="padding-bottom: 16px;">
            <h3>8. Outcome Details</h3>
            ${
  outcome.length === 0
    ? '<p>No outcome data available.</p>'
    : outcome
      .map(
        (o: any) =>
          `<div><p><strong>Pregnancy Achieved:</strong> ${
            o.pregnancy_achieved ? 'Yes' : 'No'
          }</p>
                <p><strong>Result:</strong> ${o.result ?? ''}</p></div>`
      )
      .join('')
}
        </section>
        <footer>
            <div class="end_report">
              <strong>End of Report</strong>
              <p>Note: This report is generated for informational purposes only and should not be considered as a medical advice.</p>            
            </div>        
        </footer>
        </body>
        </html>`;
  return (
    data && (
      <iframe
        title="treatmentPlanProcedure"
        width="100%"
        height="100%"
        style={{ border: 'none', overflow: 'hidden' }}
        srcDoc={html}
        ref={iframeRef} // Attach the ref to the iframe
      />
    )
  );
};

export default TreatmentPlanProcedure;
